<template>
  <div class="page-comp-clue-allocation-records">
    <special-head :title="$t('分配记录')" />

    <jw-spin v-if="data.loading" :tip="$t('数据加载中') + '……'" class="spin-loading"> </jw-spin>
    <template v-else>
      <div v-if="data.recordsList.length" class="allocation-records-container">
        <template v-for="(item, index) in data.recordsList" :key="index">
          <special-card>
            <template #title>
              <span class="date-time">{{ item?.gmtCreated }}</span>
            </template>

            <template #more>
              <xl-dot :color="recordTypeColor[item.recordType]" :title="item.recordTypeName" :isConsistent="false"></xl-dot>
              <!-- <span class="transfer-text-span">{{ item.recordTypeName }}</span> -->
              <!-- <jw-tag class="transfer-text" color="magenta" size="small">{{ item.recordTypeName }}</jw-tag> -->
            </template>

            <div class="clue-allocation-records-content" v-if="item.recordType == 10">
              <!-- 转移 -->
              <xl-avatar :avatar="{ name: item?.operateUserName || '', url: item?.operateUserAvatar, id: item?.operateUser }" :size="20" />
              <span>{{ $t('将') }}</span>
              <span class="clue-records-name">{{ item?.businessName }}</span>
              <span class="clue-records-handle">{{ $t('转移给') }}</span>
              <xl-avatar class="clue-records-avatar" :avatar="{ name: item?.newUserName || '', url: item?.newUserAvatar, id: item?.newUser }" :size="22" />,
              <span class="clue-records-handle">{{ $t('转移的原因是') }}</span>
              <span class="clue-records-bold">{{ item?.reason }}</span>
            </div>

            <div class="clue-allocation-records-content" v-else-if="item.recordType == 20">
              <!-- 领取 -->
              <xl-avatar :avatar="{ name: item?.operateUserName || '', url: item?.operateUserAvatar, id: item?.operateUser }" :size="20" />
              <span>{{ $t('领取') }}</span>
              <span class="clue-records-name">{{ item?.businessName }}</span>
            </div>

            <div class="clue-allocation-records-content" v-else-if="item.recordType == 30 || item.recordType == 32">
              <!-- 分配 -->
              <xl-avatar :avatar="{ name: item?.operateUserName || '', url: item?.operateUserAvatar, id: item?.operateUser }" :size="20" />
              <span>{{ $t('将') }}</span>
              <span class="clue-records-name">{{ item?.businessName }}</span>
              <span class="clue-records-handle">{{ $t('分配给') }}</span>
              <template v-if="item?.newUserName">
                <xl-avatar class="clue-records-avatar" :avatar="{ name: item?.newUserName || '', url: item?.newUserAvatar, id: item?.newUser }" :size="20" />
              </template>
              <span v-else class="clue-records-name">{{ item?.poolName }}</span>
            </div>

            <div class="clue-allocation-records-content" v-else-if="item.recordType == 31">
              <!-- 自动分配 -->
              <span>{{ $t('系统') }}</span>
              <span>{{ $t('将') }}</span>
              <span class="clue-records-name">{{ item?.businessName }}</span>
              <span class="clue-records-handle">{{ $t('自动分配给') }}</span>
              <template v-if="item?.newUserName">
                <xl-avatar class="clue-records-avatar" :avatar="{ name: item?.newUserName || '', url: item?.newUserAvatar, id: item?.newUser }" :size="20" />
              </template>
              <span v-else class="clue-records-name">{{ item?.poolName }}</span>
            </div>

            <div class="clue-allocation-records-content" v-else-if="item.recordType == 40">
              <!-- 退回 -->
              <xl-avatar :avatar="{ name: item?.operateUserName || '', url: item?.operateUserAvatar, id: item?.operateUser }" :size="20" />
              <span>{{ $t('将') }}</span>
              <span class="clue-records-name">{{ item?.businessName }}</span>
              <span>{{ $t('退回到') }}</span>
              <span class="clue-records-name">{{ item?.poolName }}</span
              >,
              <span class="clue-records-handle">{{ $t('退回原因是') }}</span>
              <span class="clue-records-bold">{{ item?.reason }}</span>
            </div>

            <div class="clue-allocation-records-content" v-else-if="item.recordType == 41">
              <!-- 自动退回 -->
              <span>{{ $t('系统') }}</span>
              <span>{{ $t('将') }}</span>
              <span class="clue-records-name">{{ item?.businessName }}</span>
              <span class="clue-records-handle">{{ $t('自动退回到') }}</span>
              <span class="clue-records-name">{{ item?.poolName }}</span
              >，
              <span class="clue-records-handle">{{ $t('触发自动退回规则') }}：</span>
              <span class="clue-records-bold">{{ item?.ruleName }}</span>
            </div>
          </special-card>
        </template>
      </div>
      <template v-else>
        <jw-empty class="no-data-record" :img-src="imgSvg"> {{ $t('暂无分配记录') }}</jw-empty>
      </template>
    </template>
  </div>
</template>

<script setup>
import { reactive, inject, onMounted } from 'vue';
import imgSvg from '@/assets/images/img-no-record.svg';
import { Message } from '@arco-design/web-vue';
import { leadsService } from '@/api/';
import { useI18n } from 'vue-i18n';
import SpecialHead from '@/components/fh-detail/components/special-head/index.vue';
import SpecialCard from '@/components/fh-detail/components/special-card/index.vue';

const { t } = useI18n();
const data = reactive({
  id: inject('globalParams')?.id || inject('defaultParams')?.id,
  type: inject('globalParams')?.entity || inject('defaultParams')?.entity,
  recordsList: [],
  loading: false
});

const recordTypeColor = {
  10: 'rgb(var(--green-4))', // 转移
  20: '#D91AD9', // 领取
  30: 'rgb(var(--arcoblue-5))', // 分配
  32: 'rgb(var(--arcoblue-5))', // 分配
  31: 'rgb(var(--arcoblue-5))', // 自动分配
  40: 'orange', // 退回
  41: 'orange' // 自动退回
};

const getTransferRecord = async () => {
  data.loading = true;
  try {
    const params = {
      businessId: data.id,
      type: data.type,
      page: 1,
      pageSize: 999
    };
    const json = await leadsService.getTransferRecord(params);
    data.loading = false;
    if (!json.error) {
      console.log(65, json.result);
      data.recordsList = json.result?.records;
    } else {
      Message.error(json.error?.msg || t('操作失败'));
    }
  } catch (e) {
    console.log(e);
    data.loading = false;
    Message.error(e.message || e.statusText || t('操作失败'));
  }
};
onMounted(async () => {
  await getTransferRecord();
});
defineExpose({ getTransferRecord });
</script>
<style scoped lang="less">
@import './index.less';
</style>
